import { MouseEvent, ReactNode, useRef, useState } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import { DotLegalTextField, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import MentionInput from "@common/components/mentionInput/MentionInput";
import { addControlIcon, trashcan } from "@common/icons";
import { useTranslation } from "@localization/useTranslation";
import PolicyPdfRichText from "../policyPdf/PolicyPdfRichText";
import PolicyRichTextView from "./PolicyRichTextView";
import { PolicyControl } from "./PolicySections.types";
import { resolveControls } from "./PolicySections.utils";
import PolicyControlsDialog from "./PolicyControlsDialog/PolicyControlsDialog";
import LinkedControlsBox from "./LinkedControlsBox";
import { sectionOuterPaperPadding } from "./PolicySectionsEditor.tsx";
import { usePolicySectionEditorStyles } from "./PolicySectionsEditor.styles.ts";

export interface PolicyEditableItemProps {
    numberLabel: string;
    title: string | null;
    content: string | null;
    linkedControlIds: string[];
    availableControls: Array<PolicyControl>;
    controlsHeaderKey: "controlsRequiredBySection" | "controlsRequiredBySubsection";
    setFocusOnTitle: boolean;
    disabled?: boolean;
    dragHandleProps: DraggableProvidedDragHandleProps | null;
    showInlineDragHandle?: boolean;
    titleFontSize: number;
    onTitleChange: (title: string) => void;
    onContentChange: (content: string) => void;
    onLinkedControlsChange: (linkedControlIds: string[]) => void;
    onDelete?: () => void;
    id: string;
    extraActions?: ReactNode;
    children?: ReactNode;
    isPdf?: boolean;
    // Edit-on-demand: when false the content shows as static HTML; clicking it asks the parent to make
    // this the single live editor. Only meaningful when not disabled and not in PDF mode.
    isEditingContent?: boolean;
    onStartEditingContent?: () => void;
    readOnlyPreview?: boolean;
    // Defaults to the opposite of isPdf. Set it explicitly where static rendering and PDF rendering are not
    // the same thing, such as the public sign-off page, which renders PDF markup but on a phone.
    scrollableTables?: boolean;
}

function PolicyEditableItem(props: PolicyEditableItemProps) {
    const { translateString } = useTranslation();
    const styles = usePolicySectionEditorStyles();
    const {
        numberLabel,
        title,
        content,
        linkedControlIds,
        availableControls,
        setFocusOnTitle,
        disabled = false,
        dragHandleProps,
        showInlineDragHandle,
        titleFontSize,
        onTitleChange,
        onContentChange,
        onLinkedControlsChange,
        onDelete,
        id,
        extraActions,
        children,
        isPdf = false,
        isEditingContent = false,
        onStartEditingContent,
        readOnlyPreview = false,
        scrollableTables,
    } = props;

    const [showControlsDialog, setShowControlsDialog] = useState(false);
    const caretCoordsRef = useRef<{ x: number; y: number } | null>(null);

    const handleStartEditingContent = (event: MouseEvent<HTMLDivElement>) => {
        // Switch on mousedown, not click. When switching to a section below the active one, blurring the
        // active editor collapses its toolbar and shifts this section up between mousedown and mouseup, so a
        // plain click frequently never lands here (it took two clicks to switch). Flush the active editor's
        // save via its blur handler first so nothing is lost, then make this section the live editor.
        // Measure the click offset within this content box BEFORE blurring the active editor (which shifts
        // layout), so the caret can be re-anchored accurately once this section's editor mounts.
        const rect = event.currentTarget.getBoundingClientRect();
        caretCoordsRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        event.preventDefault();
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        onStartEditingContent?.();
    };

    // Both the PDF and the read-only version preview render titles/content as static text and
    // collapse fully-empty items — only the editable view shows the title field and content input.
    const renderStatic = isPdf || readOnlyPreview;

    const hasTitle = !!title && title.trim().length > 0;
    const hasContent = !!content && content.trim().length > 0;

    if (renderStatic && !hasTitle && !hasContent) {
        return null;
    }

    const linkedControls = resolveControls(linkedControlIds, availableControls);

    return (
        <Box
            // In PDF mode long section content must be allowed to fragment across pages — an
            // unbreakable block taller than a page gets pushed and sliced by the headless browser
            className={isPdf ? undefined : "avoid-page-break"}
            id={id}
            sx={(theme) => ({
                marginBottom: 2,
                marginX: -sectionOuterPaperPadding,
                paddingX: sectionOuterPaperPadding,
                cursor: "default",
                // Keep scrollIntoView (on add) and the title autofocus from parking a new row behind the sticky action bar
                scrollMarginBottom: theme.spacing(10),
                "&:hover .policyItemActions": { visibility: "visible" },
            })}
        >
            {!isPdf && !showInlineDragHandle && (
                <Box {...(dragHandleProps ?? undefined)} aria-label="drag handle">
                    <DragIndicatorIcon
                        className="sectionDrag"
                        sx={(theme) => ({
                            position: "absolute",
                            visibility: "hidden",
                            transform: "rotate(90deg)",
                            fill: theme.palette.grey[500],
                            top: 2,
                            left: "50%",
                            right: "50%",
                            height: 20,
                            outline: "none",
                            "&:hover": { cursor: "grab" },
                            "&:active": { cursor: "grabbing" },
                        })}
                    />
                </Box>
            )}
            {renderStatic && hasTitle && (
                <Stack
                    direction="row"
                    marginBottom={1}
                    // Not the avoid-page-break class — its display:block would kill the flex row
                    sx={(theme) => ({
                        // Keep the heading in one piece and on the same page as the first lines of its content
                        breakInside: "avoid",
                        breakAfter: "avoid",
                        fontSize: theme.typography.pxToRem(titleFontSize),
                        fontWeight: theme.typography.fontWeightBold,
                    })}
                >
                    <Box sx={{ marginRight: 1, flexShrink: 0 }}>{numberLabel}.</Box>
                    <Box>{title}</Box>
                </Stack>
            )}
            {!renderStatic && (
                <Stack direction="row" alignItems="center" marginBottom={1} sx={{ position: "relative" }}>
                    {showInlineDragHandle && dragHandleProps && (
                        <Box
                            {...dragHandleProps}
                            className="policyItemActions"
                            aria-label="drag handle"
                            sx={{
                                position: "absolute",
                                right: "100%",
                                top: "50%",
                                transform: "translateY(-50%)",
                                marginRight: 0.5,
                                display: "flex",
                                alignItems: "center",
                                visibility: "hidden",
                                cursor: "grab",
                                "&:active": { cursor: "grabbing" },
                            }}
                        >
                            <DragIndicatorIcon sx={(theme) => ({ fill: theme.palette.grey[500] })} fontSize="small" />
                        </Box>
                    )}
                    <Box
                        sx={(theme) => ({
                            marginRight: 1,
                            fontSize: `${theme.typography.pxToRem(titleFontSize)}`,
                            fontWeight: theme.typography.fontWeightBold,
                            flexShrink: 0,
                            marginTop: "14px",
                            alignSelf: "flex-start",
                        })}
                    >
                        {numberLabel}.
                    </Box>
                    <Box
                        sx={(theme) => ({
                            flexGrow: 1,
                            "& .MuiInputBase-root": { fontWeight: theme.typography.fontWeightBold },
                            "& .MuiInputBase-root .MuiInputBase-input": {
                                fontSize: theme.typography.pxToRem(titleFontSize),
                            },
                        })}
                    >
                        <DotLegalTextField
                            variant={"standard"}
                            label={""}
                            value={title ?? ""}
                            onChange={onTitleChange}
                            debounce
                            multiline
                            autoFocus={setFocusOnTitle}
                            maxLength={2400}
                            disabled={disabled}
                        />
                    </Box>
                    {!disabled && (
                        <Box
                            className="policyItemActions"
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                alignSelf: "center",
                                gap: 0.5,
                                marginRight: "-5px",
                                paddingY: 1,
                                visibility: "hidden",
                                "& svg": { width: 18, height: 18 },
                            }}
                        >
                            {extraActions}
                            <DotLegalTooltip text={translateString("selectControls")}>
                                <IconButton size="small" onClick={() => setShowControlsDialog(true)} aria-label="select controls">
                                    {addControlIcon}
                                </IconButton>
                            </DotLegalTooltip>
                            {onDelete && (
                                <DotLegalTooltip text={translateString("delete")}>
                                    <IconButton size="small" onClick={onDelete} aria-label="delete">
                                        {trashcan}
                                    </IconButton>
                                </DotLegalTooltip>
                            )}
                        </Box>
                    )}
                </Stack>
            )}

            {renderStatic && hasContent && <PolicyPdfRichText html={content ?? ""} scrollableTables={scrollableTables ?? !isPdf} />}
            {/* Static HTML stands in for the editor whenever this item isn't the one being edited — both in
                view mode and in edit mode. Policy content has mentions disabled, so it carries no
                node-view-backed tags that would need a mounted ProseMirror. Only the actively-edited item
                mounts a live MentionInput, so at most one editor exists at a time. */}
            {!renderStatic && disabled && hasContent && <PolicyRichTextView html={content ?? ""} />}
            {!isPdf && !disabled && isEditingContent && (
                <MentionInput
                    disableMentions
                    multiline
                    rows={1}
                    maxRows={10000}
                    debounce
                    variant={"standard"}
                    noMargin
                    value={content ?? ""}
                    placeholder={translateString("writePolicyText")}
                    onChange={(value) => onContentChange(value)}
                    focusCaretOffset={caretCoordsRef.current}
                    disableScroll
                />
            )}
            {!isPdf && !disabled && !isEditingContent && (
                <Box onMouseDown={handleStartEditingContent} onClick={(event) => event.preventDefault()} sx={{ cursor: "text", minHeight: "1.5em" }}>
                    {hasContent ? (
                        <PolicyRichTextView html={content ?? ""} />
                    ) : (
                        <Box component="span" sx={styles.inlinePlaceholder}>
                            {translateString("writePolicyText")}
                        </Box>
                    )}
                </Box>
            )}

            {linkedControls.length > 0 && (
                <LinkedControlsBox
                    variant="highlight"
                    controls={linkedControls}
                    onRemove={disabled ? undefined : (controlId) => onLinkedControlsChange(linkedControlIds.filter((id) => id !== controlId))}
                />
            )}
            {children}
            {showControlsDialog && (
                <PolicyControlsDialog
                    title={title}
                    availableControls={availableControls}
                    selectedControlIds={linkedControlIds}
                    onSave={(ids) => {
                        onLinkedControlsChange(ids);
                        setShowControlsDialog(false);
                    }}
                    onClose={() => setShowControlsDialog(false)}
                />
            )}
        </Box>
    );
}

export default PolicyEditableItem;
